import { useState, useEffect, useRef, useMemo } from 'react';
import { withStyles } from '@material-ui/core/styles';
import { withSnackbar } from 'notistack';
import styles from './styles';
import Select from '../../../../common/StyledSelect';
import Dialog from './components/Dialog';
import { getDataWithQueryString } from '../../../../../api/componentAPIs';
import PanelAction from './components/Action';
import { compose } from 'recompose';
import CustomDateRangePicker from './components/CustomDateRangePicker';
import Messages from './Messages';
import { CircularProgress, Button } from '@material-ui/core';
import { getPreparedMessageData } from '../utils';
import TimePeriod from './components/TimePeriod';
import { useAbortController } from '../../../../common/hooks/useAbortController';
import {
  getFieldError,
  getDefaultErrorInfo,
  getQueryString,
  isValidInputs,
  getMessageHistoryGroupedByDate,
  getGroupedMessageTerminalCodes,
} from './utils';
import constants from './constants';
import ErrorMessage from './components/ErrorMessage';
import appConstants from '../../../../../appConstants';
import { unionBy } from "lodash";

const MessageHistory = (props) => {
  const { getNewAbortSignal, isMountedRef } = useAbortController();
  const [messageHistory, setMessageHistory] = useState([]);
  const [selectedDriverId, setSelectedDriverId] = useState(props.defaultDriver || null);
  const [selectedDateRange, setSelectedDateRange] = useState(props.defaultDateRange);
  const [selectedTimePeriod, setSelectedTimePeriod] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isDisabled, setIsDisabled] = useState(true);
  const [apiError, setAPIError] = useState(null);
  const [errorInfo, setErrorInfo] = useState(getDefaultErrorInfo());
  const driverInfoRef = useRef(props.driverInfo);
  const driverListRef = useRef(props.driverOptions);
  const terminalsRef = useRef(props.defaultTerminalCode);

  useEffect(() => {
    loadMessages();
  }, []);

  driverListRef.current = useMemo(() => {
    return unionBy(props.driverOptions, driverListRef.current, 'value');
  }, [props.driverOptions, driverListRef.current]);

  const loadMessages = () => {
    if (driverListRef.current?.length) {
      fetchMessageHistory();
      return;
    }
    setAPIError(constants.API_MESSAGES.DRIVERS_NOT_AVAILABLE);
    setIsLoading(false);
  };

  const hasError = () => {
    let errorInfo = {};
    [
      { field: 'selectedDriverId', value: selectedDriverId },
      { field: 'selectedDateRange', value: selectedDateRange },
      { field: 'selectedTimePeriod', value: selectedTimePeriod }
    ].forEach((inputState) => {
      errorInfo[inputState.field] = getFieldError(inputState.field, inputState.value);
    });
    if (isValidInputs(errorInfo)) {
      setIsLoading(true);
      return false;
    }
    setErrorInfo(errorInfo);
    return true;
  };

  const fetchMessageHistory = () => {
    const queryParam = getQueryString({
      driverNumber: driverListRef.current?.find((option) => option.value === selectedDriverId)
        ?.driverNumber,
      selectedDateRange,
      selectedTimePeriod
    });
    if (!hasError()) {
      const signal = getNewAbortSignal();
      getDataWithQueryString('MessageHistory', queryParam, { signal }).then(
        (response) => {
          if (isMountedRef.current) {
            if (Math.trunc(response.status / 100) === 2) {
              handleSuccess(response.data);
              return;
            }
            handleFailed();
          }
        },
        (_error) => {
          handleFailed();
        }
      );
    }
  };

  const getDriverNameFromResponse = (responseData) => {
    const driverName = responseData[0]?.driverName;
    //all message should have driverName, for old message there is chances that driverName may not exist then check if recent message has driverName
    return driverName ? driverName : responseData.find((res) => res.driverName)?.driverName;
  };

  const handleSuccess = (responseData) => {
    const preparedData = getPreparedMessageData(responseData);
    if (preparedData.length === 0) {
      setAPIError(constants.API_MESSAGES.NOT_FOUND);
      setMessageHistory([]);
      setDriversTerminal([]);
    } else {
      const selectedDriverName = getDriverNameFromResponse(responseData);
      updateDriverOptions(selectedDriverName);
      setDriversTerminal(responseData);
      const messageHistoryGroupedByDate = getMessageHistoryGroupedByDate(
        preparedData,
        props.defaultTerminalOffset
      );
      setMessageHistory(messageHistoryGroupedByDate);
      setAPIError(null);
    }
    setIsLoading(false);
    setIsDisabled(true);
  };

  const updateDriverOptions = (selectedDriverName) => {
    if (driverInfoRef.current?.name) {
      return;
    }
    driverListRef.current = driverListRef.current.map((option) => {
      if (selectedDriverId === option.value) {
        return {
          ...option,
          driverName: selectedDriverName,
          labelSuffix: selectedDriverName?`(${selectedDriverName})`: ''
        };
      }
      return option;
    });
    setDriverInfoRef(selectedDriverName);
  };

  const handleFailed = () => {
    setMessageHistory(null);
    setAPIError(constants.API_MESSAGES.FAILED_TO_FETCH);
    setIsLoading(false);
    setIsDisabled(false);
  };

  const setDriversTerminal = (responseData) => {
    terminalsRef.current = props.defaultTerminalCode;
    const terminals = getGroupedMessageTerminalCodes(responseData);
    if (terminals) {
      terminalsRef.current = terminals;
    }
  };

  const onDriverIdChange = (option) => {
    const value = option ? option.value : null;
    const matchedDriver = driverListRef.current?.find(
      (option) => option.value == value
    );
    if (matchedDriver || !value) {
      handleDriverChange(option);
    } else {
      updateDriverNumber(value);
    }
  }

  const updateDriverNumber = (newDriverId) => {
    const newDriverOption = {
      label: `#${newDriverId}`,
      labelSuffix: "",
      value: newDriverId,
      driverNumber: newDriverId,
    };
    driverListRef.current.push(newDriverOption);
    handleDriverChange(newDriverOption);
  };

  const handleDriverChange = (option) => {
    const driverId = option ? option.value : null;
    setDriverInfoRef(option?.driverName);
    setSelectedDriverId(driverId);
    setErrorInfo((prevErrorInfo) => ({
      ...prevErrorInfo,
      selectedDriverId: getFieldError('selectedDriverId', driverId)
    }));
    setIsDisabled(false);
  };

  const onChangeDateRange = (dateRange) => {
    setSelectedDateRange(dateRange);
    setErrorInfo((prevErrorInfo) => ({
      ...prevErrorInfo,
      selectedDateRange: getFieldError('selectedDateRange', dateRange)
    }));
    setIsDisabled(false);
  };

  const setDriverInfoRef = (driverName = '') => {
    driverInfoRef.current = { name: driverName };
  };

  const onChangeTimePeriod = (hour) => {
    const updatedHour = hour ?? null;
    if (updatedHour !== appConstants.INVALID_TIME_RANGE) {
      setSelectedTimePeriod(updatedHour);
    }
    setErrorInfo((prevErrorInfo) => ({
      ...prevErrorInfo,
      selectedTimePeriod: getFieldError('selectedTimePeriod', updatedHour)
    }));
    setIsDisabled(false);
  };

  const { classes, onClose, isDriverChangeDisabled = false } = props;
  const disabledClass = isDriverChangeDisabled
    ? `${classes.disableStyle} ${classes.disabledInput}`
    : '';
  return (
    <Dialog handleClose={onClose}>
      <div className={`${classes.headerContainer} ${isLoading ? classes.disabledInput : ''}`}>
        <div className={classes.terminalContainer}>
          <span className={classes.terminalLabel}>Terminal:</span>
          <span className={classes.terminalValue}>{terminalsRef.current}</span>
        </div>
        <div className={classes.inputContainer}>
          <div className={`${classes.flexRow} ${disabledClass}`}>
            <Select
              textFieldProps={{
                id: 'selectedDriverId',
                label: 'Driver',
                variant: 'outlined',
                helperText: errorInfo.selectedDriverId,
                InputLabelProps: {
                  classes: {
                    root: classes.inputLabel
                  }
                }
              }}
              InputPropsClasses={{
                input: classes.inputField
              }}
              value={selectedDriverId}
              onChange={onDriverIdChange}
              error={Boolean(errorInfo.selectedDriverId)}
              data={driverListRef.current}
              isDisabled={isDriverChangeDisabled}
              rootClass={classes.selectRoot}
              canAcceptNewValue
              createLabel="Enter Driver Number"
              newValueParams={constants.DRIVER_NUMBER_PARAMS}
            />
          </div>
          <CustomDateRangePicker
            selectedDateRange={selectedDateRange}
            onChangeDateRange={onChangeDateRange}
            errorInfo={errorInfo}
            labelText="Date Range"
            staticRanges={constants.STATIC_DATE_RANGE_LIST}
            maxDate={new Date()}
          />
          <TimePeriod
            onChangeTimePeriod={onChangeTimePeriod}
            errorInfo={errorInfo}
            selectedTimePeriod={selectedTimePeriod}
          />
        </div>
        <Button
          variant="contained"
          size="small"
          color="primary"
          onClick={fetchMessageHistory}
          disabled={isDisabled}
          classes={{
            root: classes.actionButton,
            disabled: classes.disabledButton
          }}
          data-automationid="search">
          Search
        </Button>
      </div>
      <div className={`${classes.messageContainer} customScroll`}>
        {isLoading && (
          <span className={classes.loaderContainer}>
            <CircularProgress size={24} className={classes.loader} />
            {constants.API_MESSAGES.FETCH_IN_PROGRESS}
          </span>
        )}
        {!isLoading && apiError && (
          <ErrorMessage
            onClick={fetchMessageHistory}
            message={apiError}
            showRetryButton={!messageHistory}
          />
        )}
        {!isLoading && !apiError && (
          <Messages
            messageHistory={messageHistory}
            driverInfo={ driverInfoRef?.current }
            terminalOffset={props.defaultTerminalOffset}
          />
        )}
      </div>
      <PanelAction handleClose={onClose} isLoading={isLoading} />
    </Dialog>
  );
};

export default compose(withStyles(styles), withSnackbar)(MessageHistory);
